import { Component } from '@angular/core';
import { AccountScreen, AccountVestigingenRow, getAccountVestigingen } from '../account-screen';
import { createColumnDef, createModel, TableModel } from '../../shared/components/table/table/table.model';
import { combineLatest, Observable, of } from 'rxjs';
import { nonBreakingSpace } from '../../services/labels';
import { TeamToekennenData, TeamToekennenDialogComponent } from '../../dialogs/account/team-toekennen-dialog/team-toekennen-dialog.component';
import { map, switchMap } from 'rxjs/operators';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { TableComponent } from '../../shared/components/table/table/table.component';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { InstantSearchBoxComponent, MultiSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { LicentieRol, RCumLaudeAccount, RRol, RVestiging } from '@cumlaude/service-contract';
import { IconComponent } from '@cumlaude/shared-components-icons';

interface AccountTeamRow extends AccountVestigingenRow {
	autorisaties: string;
}

function makeRow(account: RCumLaudeAccount, alleVestigingen: RVestiging[]): AccountTeamRow {
	const { niveaus, leerjaren, klassen } = account;
	const autorisaties = [...niveaus, ...leerjaren.map(String), ...klassen].join(', ');
	const vestigingen = getAccountVestigingen(account, alleVestigingen);
	return { account, autorisaties, vestigingen };
}

@Component({
	selector: 'app-team',
	templateUrl: '../account-screen.html',
	styleUrls: ['../account-screen.scss'],
	imports: [
		InstantSearchBoxComponent,
		ButtonComponent,
		TooltipDirective,
		TableComponent,
		AsyncPipe,
		TitleCasePipe,
		MultiSelectDropdownComponent,
		IconComponent,
	],
})
export class TeamComponent extends AccountScreen<AccountTeamRow> {
	override getRol() {
		return RRol.TEAMLEIDER;
	}

	override getLicentieRol(): LicentieRol {
		return LicentieRol.SCHOOLBREEDTEAM;
	}

	override getRolLabel() {
		return 'Teamleider';
	}

	override getRolDescription() {
		return 'Teamleiders kunnen alle gegevens zien van de afdeling op de vestiging waar zij aan gekoppeld zijn.';
	}

	makeAccountTableModel(accounts: RCumLaudeAccount[]): Observable<TableModel<AccountTeamRow>> {
		return this.alleVestigingen$.pipe(
			map((vestigingen) => {
				const model = createModel(
					accounts.map((account) => makeRow(account, vestigingen)),
					(row) => row.account.id!
				);
				const bewerken = this.createActionColumn(
					'edit',
					'Rol bewerken'.replace(' ', nonBreakingSpace),
					'edit',
					({ account }) => this.openTeamToekennenDialog(account),
					() => true
				);
				model.columnDefs = [
					this.getNaamColumn(),
					createColumnDef('vestigingen', 'Vestigingen'),
					createColumnDef('autorisaties', 'Teamleider'),
					bewerken,
					this.createRolIntrekkenColumn(),
				];
				return model;
			})
		);
	}

	override openRolToekennenDialog() {
		this.openTeamToekennenDialog();
	}

	openTeamToekennenDialog(account?: RCumLaudeAccount) {
		const allAccounts = account
			? of([account])
			: this.getVestigingen().pipe(
					switchMap((vestigingen) => this.restService.getAccountsFromFilter({ vestiging: vestigingen, excludeRol: RRol.TEAMLEIDER }))
				);
		combineLatest([allAccounts, this.dataService.getKlassen()]).subscribe(([accounts, alleKlassen]) => {
			const data: TeamToekennenData = {
				account,
				alleAccounts: accounts,
				alleKlassen,
			};
			const dialogRef = this.dialog.open<RCumLaudeAccount>(TeamToekennenDialogComponent, { data });
			dialogRef.closed.subscribe((editedAccount) => {
				if (!editedAccount) return;
				this.resetFilter();
				editedAccount.rollen.push(this.getRol());
				this.accountsService.update$.next([
					editedAccount,
					() => {
						if (!account) this.accountsService.createLocal$.next(editedAccount);
					},
					(err) => {
						this.toastr.error(`Er ging iets fout bij het toekennen van rollen: ${err.error}`);
					},
				]);
			});
		});
	}
}
